import { useQuery } from '@tanstack/react-query';
import {
  DISCIPLINES_STATUS_SA_QUERY_KEY,
  OBRA_QUERY_KEY,
} from 'Data/keys/QueryKeys';
import ObrasApi from 'Data/Obras.api';
import { IDisciplineSAStatusRequest } from 'Data/interfaces/Obra/IDisciplineSAStatusRequest';
import {
  FileStatusInStorageEnum,
  IDisciplineSAStatusResponse,
} from 'Data/interfaces/Obra/IDisciplineSAStatusResponse';
import { queryClient } from 'Context/QueryClientContext';
import { useEffect } from 'react';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import toastHandler from 'Utils/toastHandler';
import { AxiosError } from 'axios';
import { DisciplineFolderStatusEnum } from 'Data/interfaces/Obra/IDisciplineResponse';

export const useQueryDisciplinesStatusSA = (props: IDisciplineSAStatusRequest) => {
  const { data, refetch, isLoading, isFetching, error } = useQuery({
    queryKey: [
      DISCIPLINES_STATUS_SA_QUERY_KEY,
      props.csId,
      props.ConstructionSiteDisciplineId,
    ],
    staleTime: 60 * 5 * 1000, // 5 minutes
    retry: false,
    queryFn: async () => {
      const response = await ObrasApi.statusSADiscipline(props);
      const returnResponse: IDisciplineSAStatusResponse = {
        ConstructionSiteDisciplineId: props.ConstructionSiteDisciplineId,
        disciplineSAStatus: response.data.data,
        existingFolderApiId: response.data?.info || undefined,
      };
      return returnResponse;
    },
    enabled: !!props.csId && !!props.fileApiId && !!props.ConstructionSiteDisciplineId,
  });

  useEffect(() => {
    const handleUpdateObra = async (csId: number, disciplineId: number) => {
      await queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY] });
      await queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY, Number(csId)] });
      queryClient.setQueryData<IObraResponse[]>([OBRA_QUERY_KEY], (old) => {
        return old?.map((obra) => {
          if (obra.ConstructionSiteId === csId) {
            if (obra.Disciplines && obra.Disciplines.length > 0) {
              const disciplineRestoreIndex = obra.Disciplines.findIndex((discipline: any) => {
                return discipline.ConstructionSiteDisciplinesId === disciplineId;
              });
              if (disciplineRestoreIndex !== -1) {
                obra.Disciplines[disciplineRestoreIndex].FolderStatus = DisciplineFolderStatusEnum.Sync;
              }
            }
          }
          return obra;
        });
      });
      queryClient.setQueryData<IObraResponse>([OBRA_QUERY_KEY, Number(csId)], (old) => {
        if (old) {
          return {
            ...old,
            Disciplines: old.Disciplines?.map((discipline: any) => {
              if (discipline.ConstructionSiteDisciplinesId === disciplineId) {
                return {
                  ...discipline,
                  FolderStatus: DisciplineFolderStatusEnum.Sync,
                };
              }
              return discipline;
            }),
          };
        }
        return old;
      });
    };
    if (
      data && !error &&
      data.disciplineSAStatus === FileStatusInStorageEnum.Ok &&
      !isLoading && !isFetching
    ) {
      handleUpdateObra(props.csId, data.ConstructionSiteDisciplineId);
    }
  }, [data, error, isLoading, isFetching, props.csId]);

  useEffect(() => {
    if (!data && error && error instanceof AxiosError && !isLoading && !isFetching) {
      const errorMsg = error?.response?.data?.errors
        ? error?.response?.data?.errors[0].Message
        : 'Erro ao carregar status da disciplina';
      toastHandler.showError(errorMsg);
    }
  }, [data, error, isLoading, isFetching]);

  return {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
  };
};
